import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { getConversations, getMessages, sendMessage } from '../api/messages';
import { 
    FaPaperPlane, FaPaperclip, FaFile, FaSearch, FaCircle, 
    FaCheck, FaCheckDouble, FaEllipsisV, FaArrowLeft 
} from 'react-icons/fa';
import { useSocket } from '../context/SocketContext';
import loadImage from '../assets/imageLoader';

function MessagesPage() {
    const { user } = useAuth();
    const { socket, setUnreadMessageCount } = useSocket();
    const location = useLocation();

    // Clear unread count when page loads
    useEffect(() => {
        if (setUnreadMessageCount) {
            setUnreadMessageCount(0);
        }
    }, [setUnreadMessageCount]);
    
    // Data States
    const [conversations, setConversations] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    
    // UI States
    const [searchTerm, setSearchTerm] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [isMobileListVisible, setIsMobileListVisible] = useState(true); // For mobile responsiveness
    const [onlineUsers, setOnlineUsers] = useState(new Set());

    // Refs
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const prevMessagesLength = useRef(0);
    const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // --- Initial Load & Socket Listeners ---
    useEffect(() => {
        fetchConversations();
        
        if (socket) {
            socket.on('online_users_list', (users) => setOnlineUsers(new Set(users)));
            socket.on('user_status_change', ({ userId, status }) => {
                setOnlineUsers(prev => {
                    const newSet = new Set(prev);
                    status === 'online' ? newSet.add(userId) : newSet.delete(userId);
                    return newSet;
                });
            });

            socket.on('new_message', (msg) => {
                // If this message belongs to the active chat, add it
                if (activeChat && (msg.senderId === activeChat.id || msg.recipientId === activeChat.id)) {
                    setMessages(prev => {
                        if (prev.find(m => m.id === msg.id)) return prev;
                        return [...prev, msg];
                    });
                }
                // Always refresh conversations list to update "Last Message" preview
                fetchConversations();
            });
        }

        return () => {
            if (socket) {
                socket.off('online_users_list');
                socket.off('user_status_change');
                socket.off('new_message');
            }
        };
    }, [user, socket, activeChat]);

    // --- Handle Direct Chat Start from Navigation ---
    useEffect(() => {
        if (location.state?.startChatWith) {
            const target = location.state.startChatWith;
            const existing = conversations.find(c => c.user.id === target.id);
            setActiveChat(existing ? existing.user : { id: target.id, name: target.name, picture: target.picture });
            setIsMobileListVisible(false);
        }
    }, [location.state, conversations]);

    // --- Load Messages for Active Chat ---
    useEffect(() => {
        if (activeChat) {
            fetchMessages(activeChat.id);
        } else {
            setMessages([]);
        }
    }, [activeChat]);

    // --- Auto Scroll Logic ---
    useEffect(() => {
        if (!scrollContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
        const isNew = messages.length > prevMessagesLength.current;

        if (prevMessagesLength.current === 0 || (isNew && isNearBottom)) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
        prevMessagesLength.current = messages.length;
    }, [messages]);

    // --- API Calls ---
    const fetchConversations = async () => {
        try {
            const res = await getConversations();
            setConversations(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchMessages = async (id) => {
        try {
            const res = await getMessages(id);
            setMessages(res.data);
        } catch (err) { console.error(err); }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if ((!newMessage.trim() && !attachment) || !activeChat) return;

        const formData = new FormData();
        formData.append('recipientId', activeChat.id);
        if (newMessage.trim()) formData.append('content', newMessage);
        if (attachment) formData.append('file', attachment);

        try {
            await sendMessage(formData);
            setNewMessage('');
            setAttachment(null);
            fetchMessages(activeChat.id);
            fetchConversations();
        } catch (err) { alert("Failed to send."); }
    };

    // --- Render Helpers ---
    const filteredConversations = conversations.filter(c => 
        c.user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatTime = (date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return (
        <div className="h-[calc(100vh-80px)] bg-slate-100 flex overflow-hidden font-sans">
            
            {/* Sidebar (Conversations) */}
            <div className={`w-full md:w-80 lg:w-96 bg-white border-r border-slate-200 flex-col ${isMobileListVisible ? 'flex' : 'hidden md:flex'}`}>
                
                {/* Sidebar Header */}
                <div className="p-5 border-b border-slate-100">
                    <h2 className="text-2xl font-black text-slate-800 mb-4 tracking-tight">Chats</h2>
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Search people..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-50 border-none rounded-xl py-3 pl-10 pr-4 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-1">
                    {filteredConversations.length > 0 ? (
                        filteredConversations.map(conv => (
                            <div 
                                key={conv.user.id}
                                onClick={() => { setActiveChat(conv.user); setIsMobileListVisible(false); }}
                                className={`p-3 rounded-2xl cursor-pointer transition-all flex items-center space-x-4 ${activeChat?.id === conv.user.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                            >
                                <div className="relative">
                                    <img src={loadImage(conv.user.picture)} alt={conv.user.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" />
                                    {onlineUsers.has(String(conv.user.id)) && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-baseline">
                                        <h4 className={`text-sm truncate ${activeChat?.id === conv.user.id ? 'font-black text-emerald-900' : 'font-bold text-slate-700'}`}>{conv.user.name}</h4>
                                        <span className="text-[10px] text-slate-400 font-medium">{formatTime(conv.lastMessage.createdAt)}</span>
                                    </div>
                                    <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? 'font-bold text-slate-800' : 'text-slate-500'}`}>
                                        {conv.lastMessage.isOwnMessage && 'You: '}
                                        {conv.lastMessage.content || 'Sent an attachment'}
                                    </p>
                                </div>
                                {conv.unreadCount > 0 && (
                                    <div className="w-5 h-5 bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full shadow-sm">
                                        {conv.unreadCount}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-10 text-slate-400 text-sm font-medium">No conversations found.</div>
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col bg-slate-50/50 relative ${!isMobileListVisible ? 'flex' : 'hidden md:flex'}`}>
                
                {activeChat ? (
                    <>
                        {/* Chat Header */}
                        <div className="bg-white/80 backdrop-blur-md p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 shadow-sm">
                            <div className="flex items-center space-x-4">
                                <button onClick={() => setIsMobileListVisible(true)} className="md:hidden text-slate-500 hover:text-emerald-600">
                                    <FaArrowLeft />
                                </button>
                                <img src={loadImage(activeChat.picture)} alt={activeChat.name} className="w-10 h-10 rounded-full object-cover shadow-sm" />
                                <div>
                                    <h3 className="font-bold text-slate-800 text-sm md:text-base">{activeChat.name}</h3>
                                    <p className="text-xs text-emerald-600 font-bold flex items-center">
                                        <FaCircle size={6} className={`mr-1.5 ${onlineUsers.has(String(activeChat.id)) ? 'text-green-500' : 'text-slate-300'}`} />
                                        {onlineUsers.has(String(activeChat.id)) ? 'Online' : 'Offline'}
                                    </p>
                                </div>
                            </div>
                            <button className="text-slate-400 hover:text-slate-600"><FaEllipsisV /></button>
                        </div>

                        {/* Messages Feed */}
                        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 custom-scrollbar">
                            {messages.map((msg, idx) => {
                                const isMe = msg.senderId == user?.profileId;
                                
                                return (
                                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`flex flex-col max-w-[80%] md:max-w-[60%] ${isMe ? 'items-end' : 'items-start'}`}>
                                            <div className={`px-5 py-3 rounded-2xl shadow-sm text-sm leading-relaxed relative group ${
                                                isMe 
                                                ? 'bg-emerald-600 text-white rounded-br-sm' 
                                                : 'bg-white text-slate-700 border border-slate-100 rounded-bl-sm'
                                            }`}>
                                                {msg.attachmentUrl && (
                                                    <div className="mb-2 rounded-lg overflow-hidden">
                                                        {msg.attachmentType === 'image' ? (
                                                            <a href={`${baseURL}${msg.attachmentUrl}`} target="_blank" rel="noreferrer"><img src={`${baseURL}${msg.attachmentUrl}`} className="max-w-full h-auto max-h-60 object-cover" alt="Attachment" /></a>
                                                        ) : (
                                                            <a href={`${baseURL}${msg.attachmentUrl}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline bg-black/10 p-2 rounded text-xs"><FaFile /> Download File</a>
                                                        )}
                                                    </div>
                                                )}
                                                {msg.content}
                                                <div className={`text-[10px] mt-1 flex items-center justify-end gap-1 opacity-70 ${isMe ? 'text-emerald-100' : 'text-slate-400'}`}>
                                                    {formatTime(msg.createdAt)}
                                                    {isMe && (msg.isRead ? <FaCheckDouble /> : <FaCheck />)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Input Area */}
                        <div className="p-4 bg-white border-t border-slate-200">
                            <form onSubmit={handleSendMessage} className="flex items-end gap-2 max-w-4xl mx-auto">
                                <button 
                                    type="button" 
                                    onClick={() => fileInputRef.current.click()} 
                                    className="p-3 bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-colors"
                                >
                                    <FaPaperclip />
                                </button>
                                <input type="file" ref={fileInputRef} className="hidden" onChange={e => setAttachment(e.target.files[0])} />

                                <div className="flex-1 bg-slate-100 rounded-[1.5rem] px-4 py-2 flex flex-col justify-center min-h-[48px]">
                                    {attachment && (
                                        <div className="flex justify-between items-center text-xs bg-white p-1.5 rounded-lg mb-1 border border-slate-200">
                                            <span className="truncate max-w-[150px] font-medium text-slate-600">{attachment.name}</span>
                                            <button onClick={() => setAttachment(null)} className="text-red-500 font-bold px-2">×</button>
                                        </div>
                                    )}
                                    <textarea
                                        value={newMessage}
                                        onChange={e => setNewMessage(e.target.value)}
                                        onKeyDown={e => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); }}}
                                        placeholder="Type your message..."
                                        rows={1}
                                        className="w-full bg-transparent border-none outline-none text-sm text-slate-800 placeholder:text-slate-400 resize-none max-h-32"
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={!newMessage.trim() && !attachment} 
                                    className="p-3 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-200 transition-all transform active:scale-95"
                                >
                                    <FaPaperPlane />
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 opacity-50">
                        <div className="w-24 h-24 bg-slate-200 rounded-full flex items-center justify-center mb-6">
                            <FaPaperPlane className="text-slate-400 text-4xl" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-600">Your Messages</h3>
                        <p className="text-slate-500 mt-2">Select a conversation from the left to start chatting.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MessagesPage;
